export { firstPartyRewrite, fetchFirstParty, xhrRequest };
export type { TransportOptions };

// First-party transport for the collector's API calls.
//
// Privacy/ad blockers on the visitor's device (~24% of webview traffic, July
// 2026 lead-loss incident) drop cross-site requests to *.app.protodeal.com —
// lead submits included. The multi-tenant CloudFront distributions expose the
// same services under same-origin paths on every funnel/website domain
// (/pd-api/* behaviors, see api-content infra), which blockers cannot
// distinguish from the page itself.
//
// Strategy proven live by the v4 experiment (28-30/7): first-party first,
// original absolute URL as fallback. The fallback fires on ANY failure of the
// first-party attempt (network error or non-ok response) so pages served from
// domains without the /pd-api behaviors — previews, editor, legacy — keep
// working exactly as before; there the extra attempt costs one 4xx.
//
// House rule (lesson from the incident): no critical-path request may depend
// on a cross-site resource; and every collector fetch carries
// `keepalive: true` — a webview navigating/closing with a POST in flight
// cancels it otherwise (callers pass it via init; helpers here do not strip it).

// Hostname label → same-origin path prefix served by the /pd-api/* behaviors.
// Matched on the first DNS label so dev (lead.dev.protodeal.com) and prod
// (lead.app.protodeal.com) both rewrite without environment-specific config.
// Path names are NEUTRAL on purpose (no "calendly"/"vsl" — blocker lists
// match on those words).
const FIRST_PARTY_PREFIXES: Record<string, string> = {
  lead: "/pd-api/lead",
  "landing-assistant": "/pd-api/config",
  events: "/pd-api/events",
  "calendly-data": "/pd-api/schedule",
  "vsl-serving": "/pd-api/media",
};

// Returns the same-origin equivalent of an absolute collector endpoint URL, or
// null when the URL is not one of the first-party-routed services.
function firstPartyRewrite(url: string): string | null {
  const match = /^https:\/\/([a-z0-9-]+)\.[^/]+(\/.*)?$/.exec(url);
  if (!match) {
    return null;
  }
  const prefix = FIRST_PARTY_PREFIXES[match[1]];
  if (!prefix) {
    return null;
  }
  return prefix + (match[2] || "");
}

interface TransportOptions {
  // "xhr" routes the request through XMLHttpRequest instead of fetch().
  // Hypothesis under measurement (July 2026): some webview failures are
  // specific to the fetch path (e.g. the shared 64KB keepalive body quota
  // rejects with TypeError; GTM's beacon/img transports pass while our fetch
  // dies) — retries switch transport to find out. The app_transport tag on
  // the retry reports tells us whether XHR actually rescues anything.
  transport?: "fetch" | "xhr";
}

// XMLHttpRequest wrapped as a minimal Response-like promise. Only the surface
// the collector actually consumes (ok/status/statusText/json/text) is
// implemented. keepalive has no XHR equivalent (that difference is the point);
// an AbortSignal in init is honored.
function xhrRequest(url: string, init: RequestInit): Promise<Response> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open((init.method || "GET").toUpperCase(), url, true);

    const headers = (init.headers || {}) as Record<string, string>;
    for (const name of Object.keys(headers)) {
      xhr.setRequestHeader(name, headers[name]);
    }

    if (init.signal) {
      init.signal.addEventListener("abort", () => xhr.abort());
    }

    xhr.onload = () => {
      const body = xhr.responseText;
      const responseLike = {
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        statusText: xhr.statusText,
        headers: {
          get: (name: string) => xhr.getResponseHeader(name),
        },
        json: () => {
          try {
            return Promise.resolve(JSON.parse(body));
          } catch (e) {
            return Promise.reject(e);
          }
        },
        text: () => Promise.resolve(body),
      };
      resolve(responseLike as unknown as Response);
    };
    xhr.onerror = () => reject(new TypeError("xhr network error"));
    xhr.ontimeout = () => reject(new TypeError("xhr timeout"));
    xhr.onabort = () => reject(new DOMException("aborted", "AbortError"));

    xhr.send((init.body as XMLHttpRequestBodyInit | null) ?? null);
  });
}

// A non-ok response that CAME FROM one of our fleet services is a real answer
// (validation error, booking not found...) and must be returned as-is — the
// fleet always answers JSON. Only non-ok responses that do NOT look like a
// service (S3 XML / CloudFront HTML error pages, i.e. a domain without the
// /pd-api behaviors: previews, editor, legacy) should trigger the cross-site
// fallback. Falling back on every non-ok doubled legitimate 4xx calls and, for
// blocked users, turned a valid 404 answer into a network failure.
function isServiceResponse(response: Response): boolean {
  try {
    const contentType = response.headers?.get?.("content-type") || "";
    return contentType.toLowerCase().includes("json");
  } catch (e) {
    return false;
  }
}

// fetch() that prefers the same-origin route and falls back to the original
// absolute URL if the first-party attempt fails at the network level or the
// route is not served on this domain. Both attempts are plain POSTs of the
// same body; the receiving services only report success via an ok response,
// so a failed first attempt has not been processed and the fallback cannot
// double-submit. With options.transport = "xhr" both attempts go through
// XMLHttpRequest instead of fetch (see TransportOptions).
async function fetchFirstParty(
  url: string,
  init: RequestInit,
  options?: TransportOptions,
): Promise<Response> {
  const request = options?.transport === "xhr" ? xhrRequest : fetch;
  const rewritten = firstPartyRewrite(url);
  if (!rewritten) {
    return request(url, init);
  }
  try {
    const response = await request(rewritten, init);
    if (!response.ok && !isServiceResponse(response)) {
      return request(url, init);
    }
    return response;
  } catch (e) {
    return request(url, init);
  }
}
